/**
 * KICKSTARTER MODE — a server-wide switch for the weeks the game is playable as a DEMO during its Kickstarter
 * campaign: players can try it, but not play all of it before launch. An admin turns it on in the admin
 * console (Game settings); every client learns it from /api/config at boot.
 *
 * WHAT IT DOES TODAY: caps all three skill tracks — Gardening, Fishing, Bug Catching — at `levelCap` (15 by
 * default), and shows a notice on entering the game that lists every limit in force (`kickstarterLimits`).
 * New limits belong in THIS file: add a field to the config, enforce it where it applies, and give it a line
 * in `kickstarterLimits` — the notice then explains it with no other change, so a player is never surprised
 * by a limit the notice did not mention.
 *
 * THE CAP ONLY STOPS GAINS. It never takes anything away: a player already past the cap (they played before
 * the mode was switched on) keeps every level they have and simply earns no more XP on that track. XP earned
 * at the cap by PLAYING is not banked — the demo is a demo, and a pile of hidden XP that floods out at launch
 * would be a second, invisible progression to explain. The one exception is a ONE-TIME reward (an
 * achievement's or a point-track rung's XP): that can never be earned again, so the part the cap would cut is
 * HELD on the save (`ksHeldXp`, `heldByCap`) and paid the first time the player loads with the mode off.
 *
 * THE SERVER ENFORCES THE CAP TOO (server/index.js `sanitise`, opts.ksLevelCap): a save may not carry a track
 * past max(what it had, the cap's XP) while the mode is on, so a modified client gains nothing.
 *
 * Mirrored on the server by `coerceKickstarter` in server/index.js — keep the two in step.
 */

import { xpForLevel, fishXpForLevel, bugXpForLevel, MAX_LEVEL } from "./level";

export interface KickstarterConfig {
  /** Is the mode on? Off = every other field is ignored. */
  on: boolean;
  /** The highest level any skill track can reach while the mode is on. */
  levelCap: number;
  /** The campaign page, linked from the notice ("Back us on Kickstarter"). Empty = no link. */
  campaignUrl: string;
  /** An optional line from the team, shown under the limits. Empty = none. */
  note: string;
}

/** 15 ≈ five days of an hour a day on the level curves (level.ts curve 2, ~5.5 h of gardening to L15): the
 *  first 30 plants, four shelves, Harvest All and the compost machine — with sprinklers, grow lights, the fifth
 *  shelf and the robot left for launch. The admin console can still set any cap 1..50. */
export const DEFAULT_LEVEL_CAP = 15;
export const KICKSTARTER_OFF: KickstarterConfig = { on: false, levelCap: DEFAULT_LEVEL_CAP, campaignUrl: "", note: "" };

/** Defensively read a config (untrusted JSON → a config that always answers). */
export function coerceKickstarter(raw: unknown): KickstarterConfig {
  if (!raw || typeof raw !== "object") return { ...KICKSTARTER_OFF };
  const o = raw as Record<string, unknown>;
  const cap = typeof o.levelCap === "number" && Number.isFinite(o.levelCap) ? Math.round(o.levelCap) : DEFAULT_LEVEL_CAP;
  const url = typeof o.campaignUrl === "string" && /^https?:\/\//i.test(o.campaignUrl.trim()) ? o.campaignUrl.trim().slice(0, 300) : "";
  return {
    on: o.on === true,
    levelCap: Math.max(1, Math.min(MAX_LEVEL, cap)),
    campaignUrl: url,
    note: typeof o.note === "string" ? o.note.trim().slice(0, 400) : "",
  };
}

export type SkillTrack = "garden" | "fish" | "bug";

/** The XP at which a track reaches the cap level — the most XP that track may hold while the mode is on. */
export function capXpFor(track: SkillTrack, levelCap: number): number {
  return track === "fish" ? fishXpForLevel(levelCap) : track === "bug" ? bugXpForLevel(levelCap) : xpForLevel(levelCap);
}

/**
 * THE ONE CAP RULE: what `prev` becomes after gaining `amount` under `cfg`. Off → plain addition. On → the
 * gain stops at the cap; a player already at or past it gains nothing and loses nothing.
 */
export function cappedXp(track: SkillTrack, prev: number, amount: number, cfg: KickstarterConfig): number {
  if (!(amount > 0)) return prev;
  const next = prev + amount;
  if (!cfg.on) return next;
  const cap = capXpFor(track, cfg.levelCap);
  if (prev >= cap) return prev;
  return Math.min(next, cap);
}

/** XP held for launch, per track — one-time rewards the cap would otherwise have thrown away. */
export interface HeldXp { xp: number; fishXp: number; bugXp: number }
export const NO_HELD_XP: HeldXp = { xp: 0, fishXp: 0, bugXp: 0 };
/** Each held amount is one-time reward XP, so it stays small; the ceiling only stops a hand-edited save
 *  parking a fortune here (the server applies the same one). */
export const HELD_XP_MAX = 50_000;
export function coerceHeldXp(raw: unknown): HeldXp {
  const o = raw && typeof raw === "object" ? raw as Record<string, unknown> : {};
  const n = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? Math.max(0, Math.min(HELD_XP_MAX, Math.floor(v))) : 0);
  return { xp: n(o.xp), fishXp: n(o.fishXp), bugXp: n(o.bugXp) };
}
/** How much of a ONE-TIME reward the cap would cut from `prev` — the part to hold for launch. 0 when off. */
export function heldByCap(track: SkillTrack, prev: number, amount: number, cfg: KickstarterConfig): number {
  if (!(amount > 0) || !cfg.on) return 0;
  return amount - (cappedXp(track, prev, amount, cfg) - prev);
}

/**
 * IS THIS LEVEL BEYOND THE DEMO? True while Kickstarter mode is on and `level` is above its cap — something gated there
 * cannot be reached in the demo at all, so "Unlocks at Level 25" reads as a wall the player will never get over. The
 * game says "Unlocks at full release" for those instead (and the achievements modal tags them "after the demo").
 */
export function pastDemoCap(level: number, cfg: KickstarterConfig): boolean {
  return cfg.on && level > cfg.levelCap;
}

/** "Unlocks at Level N", or "Unlocks at full release" when N is past the demo's cap (`pastDemoCap`). `prefix` names the track. */
export function unlocksAtText(level: number, cfg: KickstarterConfig, prefix = "Level"): string {
  return pastDemoCap(level, cfg) ? "Unlocks at full release" : `Unlocks at ${prefix} ${level}`;
}

/** The achievement metrics that are a skill LEVEL — the ones the demo's cap stops short of. */
const LEVEL_METRICS = new Set(["gardenLevel", "fishingLevel", "bugLevel"]);

/** Does this achievement need a level past the demo's cap (the 25 and 50 rungs while the cap is 15)? It cannot be
 *  earned during the demo, so the achievements modal tags it "after the demo" rather than leaving it to look stuck. */
export function needsLevelPastCap(conditions: ReadonlyArray<{ metric: string; threshold: number }> | undefined, cfg: KickstarterConfig): boolean {
  return !!conditions && conditions.some((c) => LEVEL_METRICS.has(c.metric) && pastDemoCap(c.threshold, cfg));
}

/** One line of the notice: a limit that is in force right now. */
export interface KickstarterLimit { icon: string; title: string; text: string }

/** Every limit in force under `cfg`, in the order the notice lists them. Empty when the mode is off. */
export function kickstarterLimits(cfg: KickstarterConfig): KickstarterLimit[] {
  if (!cfg.on) return [];
  return [
    {
      icon: "⭐",
      title: `Levels stop at ${cfg.levelCap}`,
      text: `Gardening, Fishing and Bug Catching each top out at level ${cfg.levelCap} for the demo. Anything that unlocks beyond it (plants, fish, insects and more) is waiting for the full release.`,
    },
    {
      // the server refuses every Premium Shop purchase while the mode is on (server/index.js shopPausedReply)
      icon: "🛍️",
      title: "The Premium Shop opens with the full game",
      text: "You can browse the premium plants and treasures, but nothing in the Premium Shop can be bought during the demo, with card or Petals. Garden Club opens with the full game too.",
    },
    {
      icon: "💾",
      title: "Your garden carries over",
      text: `Everything you grow, catch and collect is kept for the full release. XP past level ${cfg.levelCap} isn't kept, except achievement rewards, which are saved and paid out when the full game opens.`,
    },
  ];
}

/** A short stamp of the limits in force, so the notice re-appears when they change (sessionStorage key). */
export function kickstarterStamp(cfg: KickstarterConfig): string {
  // "ks3": Garden Club and the carry-over line were added — players who dismissed an older notice see it once
  return cfg.on ? `ks3:${cfg.levelCap}` : "ks:off";
}
